
import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export type CardPresenceRow = {
  id: string;
  board_id: string;
  card_id: string;
  user_id: string;
  user_name: string | null;
  status: "viewing" | "editing";
  updated_at: string;
};

export function useEnhancedCardPresence({
  cardId,
  boardId,
  userId,
  userName,
  status = "viewing",
}: {
  cardId: string;
  boardId: string;
  userId: string;
  userName?: string | null;
  status?: "viewing" | "editing";
}) {
  const [presence, setPresence] = useState<CardPresenceRow[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout>();
  const cleanupIntervalRef = useRef<NodeJS.Timeout>();
    const isActiveRef = useRef(true);
  const lastStatusRef = useRef<"viewing" | "editing" | null>(null);

  // Get current server-synchronized time as ISO string
  const getServerTimeISOString = useCallback(() => {
    try {
      // Use current time for now, can be enhanced with server sync later
      return new Date().toISOString();
    } catch (error) {
      console.error('Error getting server time:', error);
      return new Date().toISOString();
    }
  }, []);

  // Cleanup expired presence records (older than 30 seconds) using server time
  const cleanupExpiredPresence = useCallback(async () => {
    if (!cardId) return;
    
    try {
      // Use current time minus 30 seconds for cleanup threshold
      const thirtySecondsAgo = new Date(Date.now() - 30000).toISOString();
      
      const { error } = await supabase
        .from("card_presence")
        .delete()
        .eq("card_id", cardId)
        .lt("updated_at", thirtySecondsAgo);

      if (error) {
        console.error('❌ Error cleaning up expired presence:', error);
      } else {
        //console.log('🧹 Cleaned up expired presence records');
      }
    } catch (error) {
      console.error('❌ Exception in cleanupExpiredPresence:', error);
    }
  }, [cardId]);

  // Enhanced upsert with server time
  const upsertPresence = useCallback(
    async (newStatus: "viewing" | "editing") => {
      if (!cardId || !boardId || !userId || !isActiveRef.current) {
        return;
      }
      
      try {
        const presenceData = {
          card_id: cardId,
          board_id: boardId,
          user_id: userId,
          user_name: userName ?? "",
          status: newStatus,
          updated_at: getServerTimeISOString(),
        };

        const { error } = await supabase
          .from("card_presence")
          .upsert([presenceData], { 
            onConflict: "card_id,user_id",
            ignoreDuplicates: false 
          });

        if (error) {
          console.error('❌ Error upserting presence:', error);
        }
      } catch (error) {
        console.error('❌ Exception in upsertPresence:', error);
      }
    },
    [cardId, boardId, userId, userName, getServerTimeISOString]
  );

  // Clear presence
  const clearPresence = useCallback(async () => {
    if (!cardId || !userId) return;
    
    try {
      const { error } = await supabase
        .from("card_presence")
        .delete()
        .match({ card_id: cardId, user_id: userId });

      if (error) {
        console.error('❌ Error clearing presence:', error);
      }
    } catch (error) {
      console.error('❌ Exception in clearPresence:', error);
    }
  }, [cardId, userId]);

  // Fetch presence
  const fetchPresence = useCallback(async () => {
    if (!cardId) return;

    try {
      const { data, error } = await supabase
        .from("card_presence")
        .select("*")
        .eq("card_id", cardId);
      
      if (error) {
        console.error('❌ Error fetching presence:', error);
        return;
      }

      const safeData: CardPresenceRow[] =
        (data || []).map((row) => ({
          ...row,
          status: row.status === "editing" ? "editing" : "viewing",
        }));
      
      setPresence(safeData);
    } catch (error) {
      console.error('❌ Exception in fetchPresence:', error);
    }
  }, [cardId]);

  // Heartbeat function to keep presence alive
  /*const sendHeartbeat = useCallback(() => {
    if (isActiveRef.current) {
      upsertPresence(status);
    }
  }, [upsertPresence, status]);*/
    const sendHeartbeat = useCallback(() => {
        if (!isActiveRef.current || lastStatusRef.current === status) return;
        lastStatusRef.current = status;
        upsertPresence(status);
    }, [status, upsertPresence]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isActiveRef.current = false;
        clearPresence();
      } else {
        isActiveRef.current = true;
        if (cardId && boardId && userId) {
          upsertPresence(status);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearPresence, upsertPresence, status, cardId, boardId, userId]);

  // Subscribe to card_presence with enhanced real-time updates
  useEffect(() => {
    if (!cardId) return;

    // Initial cleanup and fetch
    cleanupExpiredPresence();
    fetchPresence();

    // Set up heartbeat (every 15 seconds)
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 15000);

    // Set up cleanup interval (every 30 seconds)
    //cleanupIntervalRef.current = setInterval(cleanupExpiredPresence, 30000);

    // Listen for realtime changes
      const channel = supabase
          .channel(`card_presence:${cardId}`)
          .on(
              "postgres_changes",
              {
                  event: "*",
                  schema: "public",
                  table: "card_presence",
                  filter: `card_id=eq.${cardId}`,
              },
              (payload) => {
                  setPresence((current) => {
                      if (payload.eventType === "DELETE" && payload.old) {
                          const removedId = payload.old.user_id;
                          return current.filter(p => p.user_id !== removedId);
                      }

                      if ((payload.eventType === "INSERT" || payload.eventType === "UPDATE") && payload.new) {
                          const incoming = payload.new as CardPresenceRow;
                          const filtered = current.filter(p => p.user_id !== incoming.user_id);
                          return [...filtered, incoming];
                      }

                      return current;
                  });
              }
          )
          .subscribe((status) => {
              setIsConnected(status === 'SUBSCRIBED');
          });

    return () => {
      // Clear intervals
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
      
      // Cleanup presence and channel
      isActiveRef.current = false;
      clearPresence();
      supabase.removeChannel(channel);
      setIsConnected(false);
    };
  }, [cardId, cleanupExpiredPresence, sendHeartbeat ]);

  // Initial presence setup
  useEffect(() => {
    if (cardId && boardId && userId) {
      isActiveRef.current = true;
      upsertPresence(status);
    }

    return () => {
      isActiveRef.current = false;
      if (cardId && userId) {
        clearPresence();
      }
    };
  }, [cardId, userId, status, boardId]);// Removed functions from the dependencies

  // Imperative API for changing status
  const setEditing = useCallback((isEditing: boolean) => {
    const newStatus = isEditing ? "editing" : "viewing";
    upsertPresence(newStatus);
  }, [upsertPresence]);

  return {
    presence,
    setEditing,
    isConnected,
  };
}
